// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  FLOATINGCONTACTBUTTON.JS - VERSION: 2025.12.14.1545                       ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.12.14.1545
// ============================================================================
// CHANGELOG:
// 2025.12.14.1545 - Created standalone floating contact button
// 2025.12.14.1545 - Sends messages to Game Admin message centre (localStorage)
// 2025.12.14.1545 - Works for logged in AND non-logged in users
// ============================================================================
import React, { useState } from 'react';
import { MessageSquare, X } from 'lucide-react';

// ============================================================================
// FLOATING CONTACT BUTTON - Works for all users, sends to admin message centre
// ============================================================================
function FloatingContactButton({ playerData, selectedGame }) {
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  const handleSendContact = () => {
    if (!contactSubject.trim() || !contactMessage.trim()) {
      alert('Please select a subject and enter your message');
      return;
    }
    
    // For non-logged in users, email is helpful but not required
    setContactSending(true);
    
    try {
      // Create message object for Game Admin message centre
      const newMessage = {
        id: Date.now(),
        type: 'player_to_admin',
        subject: contactSubject,
        message: contactMessage,
        from: playerData?.username || contactEmail || 'Guest User',
        playerId: playerData?.playerId || `guest_${Date.now()}`,
        email: contactEmail || playerData?.email || '',
        game: selectedGame?.name || 'General',
        gameId: selectedGame?.game_id || null,
        timestamp: new Date().toISOString(),
        read: false,
        replied: false
      };
      
      // Save to localStorage (Game Admin reads from here)
      const existingMessages = JSON.parse(localStorage.getItem('playerMessages') || '[]');
      existingMessages.push(newMessage);
      localStorage.setItem('playerMessages', JSON.stringify(existingMessages));
      
      console.log('📨 Contact message sent to Game Admin:', newMessage);
      
      setContactSent(true);
      setContactSubject('');
      setContactMessage('');
      setContactEmail('');
    } catch (error) {
      console.error('Contact send error:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setContactSending(false);
    }
  };

  return (
    <>
      {/* Floating Contact Button - Bottom right */}
      <button
        onClick={() => setShowContactForm(true)}
        className="fixed bottom-20 right-4 z-[9998] bg-blue-600 hover:bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-200"
        title="Contact Us"
        style={{ boxShadow: '0 4px 15px rgba(59, 130, 246, 0.5)' }}
      >
        <MessageSquare size={24} />
      </button>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-75">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-blue-500 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-xl font-bold flex items-center gap-2">
                <MessageSquare size={24} className="text-blue-400" />
                Contact Us
              </h3>
              <button
                onClick={() => {
                  setShowContactForm(false);
                  setContactSubject('');
                  setContactMessage('');
                  setContactEmail('');
                  setContactSent(false);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            {!contactSent ? (
              <div className="space-y-4">
                {/* Email field for non-logged in users */}
                {!playerData?.playerId && (
                  <div>
                    <label className="text-gray-300 text-sm block mb-2">Your Email (optional):</label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                      disabled={contactSending}
                    />
                    <p className="text-gray-500 text-xs mt-1">So we can reply to you</p>
                  </div>
                )}
                
                <div>
                  <label className="text-gray-300 text-sm block mb-2">Subject:</label>
                  <select
                    value={contactSubject}
                    onChange={(e) => setContactSubject(e.target.value)}
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                    disabled={contactSending}
                  >
                    <option value="">Select a topic...</option>
                    <option value="Bug Report">🐛 Bug Report</option>
                    <option value="Question Issue">❓ Question Issue</option>
                    <option value="Feature Request">💡 Feature Request</option>
                    <option value="Advertising">📢 Advertising</option>
                    <option value="Feedback">💬 Feedback</option>
                    <option value="Other">📝 Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-gray-300 text-sm block mb-2">Message:</label>
                  <textarea
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Type your message here..."
                    className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500 focus:outline-none h-32 resize-none"
                    disabled={contactSending}
                  />
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={handleSendContact}
                    disabled={contactSending || !contactSubject || !contactMessage.trim()}
                    className={`flex-1 ${
                      contactSending || !contactSubject || !contactMessage.trim()
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-500'
                    } text-white py-3 px-4 rounded font-bold transition-colors`}
                  >
                    {contactSending ? 'Sending...' : 'Send Message'}
                  </button>
                  <button
                    onClick={() => {
                      setShowContactForm(false);
                      setContactSubject('');
                      setContactMessage('');
                      setContactEmail('');
                    }}
                    disabled={contactSending}
                    className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 px-4 rounded font-bold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                
                <p className="text-gray-500 text-xs text-center">
                  {playerData?.playerId ? (
                    <>Logged in as: {playerData.username} (#{playerData.playerId})</>
                  ) : (
                    <>Playing as Guest</>
                  )}
                  {selectedGame?.name && <> • {selectedGame.name}</>}
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-green-500 text-6xl mb-4">✓</div>
                <p className="text-white text-lg font-bold mb-2">Message Sent!</p>
                <p className="text-gray-400 text-sm mb-4">The Game Admin will review your message.</p>
                <button
                  onClick={() => {
                    setShowContactForm(false);
                    setContactSent(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-500 text-white py-2 px-6 rounded font-bold"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default FloatingContactButton;
